/**
 * ─── STORY TIMELINE ENGINE ───────────────────────────────────────────────────
 *
 * Solves the "long conversation amnesia" problem. When a conversation is longer
 * than the recent-message window the AI sees directly, this engine builds a
 * structured timeline of everything that came before — preserving verbatim quotes
 * from emotionally and narratively significant moments.
 *
 * Philosophy:
 *   The AI doesn't need to see every message verbatim. It needs to know WHAT
 *   happened, in what order, with enough texture to feel like it was there.
 *   That means: key dialogue preserved verbatim, narrative moments described
 *   concretely, and emotional beats labeled so they can inform character state.
 *
 * Used by:
 *   - SixGuysRP.jsx   (RP stories: full history in memory, AI window = 25)
 *   - DMView.jsx      (DMs: 100 messages loaded, AI window = 60)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ─── IMPORTANCE SCORING ───────────────────────────────────────────────────────

// Keywords that signal plot-significant or emotionally significant moments.
// These messages are worth preserving verbatim even in compressed timelines.
const PLOT_KEYWORDS = /\b(love|hate|secret|confess|promise|oath|truth|reveal|trust|betray|forgive|never|always|afraid|fear|remember|forget|need|miss|bond|mate|mate|claim|mark|alpha|kill|die|alive|feel|hurt|break|heart|stay|leave|cry|tear|bleed|fight|angry|fury|sorry|apology|proposal|marriage|pregnant|child|family|protect|danger|run|escape|curse|power|ability|awaken|transform|wings|magic|soulmate|destiny|fate)\b/i;

const DIALOGUE_PATTERN = /["'"«»]|said|whispered|murmured|growled|snapped|replied|answered|told|asked|shouted|called|breathed|exhaled/i;

const INTIMATE_KEYWORDS = /\b(kiss|kisses|kissed|touch|hold|held|arms|embrace|pressed|close|skin|breath|warm|soft|gentle|tender|pulse|heartbeat|shiver|gasp|moan|lips|hands|fingers|neck|waist|naked|undress|bare|heat|desire|want|need|pleasure|pull|drag|pin|wrap|hungry|devour|claim|consume|surrender|arch|arched|tremble|trembled|whimper|breathless|slick|flush|flushed|grinding|thigh|chest|hips|spine|collarbone|jaw|temple|shoulder|wrist)\b/i;

const ACTION_RICH = /\*[^*]{10,}\*/; // has action/narration in asterisks

/**
 * Score a message by its narrative importance.
 * Higher score = more likely to be included in the timeline.
 *
 * @param {string} text - The message text
 * @param {number} index - Position in the older-messages array
 * @param {number} total - Total number of older messages
 * @returns {number}
 */
function scoreMessage(text, index, total) {
  if (!text || text.length < 30) return 0;
  let score = 0;

  // Length: longer messages contain more content
  score += Math.min(text.length / 80, 6);

  // Plot/emotional keywords
  if (PLOT_KEYWORDS.test(text)) score += 5;

  // Direct dialogue preserved verbatim
  if (DIALOGUE_PATTERN.test(text)) score += 3;

  // Intimate/physical moments
  if (INTIMATE_KEYWORDS.test(text)) score += 3;

  // Rich action/narration
  if (ACTION_RICH.test(text)) score += 2;

  // Position bonus: beginning and end of a story are most structurally important.
  // Middle messages near the boundaries also matter.
  // Shape: high at 0%, dips in middle, rises again at 100%
  const pos = index / Math.max(total - 1, 1); // 0.0 → 1.0
  const positionBonus = (1 - Math.sin(pos * Math.PI)) * 3; // peaks at start and end
  score += positionBonus;

  return score;
}

// ─── TIMELINE LABEL ───────────────────────────────────────────────────────────

/**
 * Classify a message into a beat type for timeline labeling.
 * Used to give context to timeline entries without re-reading every message.
 */
function classifyBeat(text) {
  if (!text) return '';
  if (/\b(confess|secret|truth|reveal|admit|finally|honest|tell you)\b/i.test(text)) return '[REVELATION]';
  if (/\b(promise|swear|oath|I will|I won't|I'll|never again|always)\b/i.test(text)) return '[PROMISE]';
  if (/\b(kiss|pressed.*lips|lips.*pressed|kissed)\b/i.test(text)) return '[INTIMATE]';
  if (/\b(fight|argue|angry|furious|get out|leave|storm|enough|done)\b/i.test(text)) return '[CONFLICT]';
  if (/\b(sorry|forgive|apologize|I was wrong|didn't mean)\b/i.test(text)) return '[RESOLUTION]';
  if (/\b(love|I love|love you|feel.*you|need you|want you)\b/i.test(text)) return '[EMOTIONAL PEAK]';
  if (/\b(arrived|entered|walked|found|discovered|realized)\b/i.test(text)) return '[SCENE SHIFT]';
  return '';
}

// ─── QUOTE EXTRACTION ─────────────────────────────────────────────────────────

/**
 * Extract the most quotable sentence from a message — the line most likely
 * to be a memorable piece of dialogue or narration.
 * Returns the sentence if it's worth quoting, otherwise empty string.
 */
function extractKeyQuote(text) {
  if (!text || text.length < 40) return '';

  // Split into sentences (handle RP formatting)
  const sentences = text
    .replace(/\*([^*]+)\*/g, '$1') // strip asterisks but keep content
    .split(/(?<=[.!?])\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 20);

  if (sentences.length === 0) return '';

  // Score each sentence
  const scored = sentences.map(s => {
    let score = 0;
    if (PLOT_KEYWORDS.test(s)) score += 4;
    if (INTIMATE_KEYWORDS.test(s)) score += 3;
    if (DIALOGUE_PATTERN.test(s)) score += 3;
    if (s.includes('"') || s.includes("'") || s.includes('’') || s.includes('“')) score += 2;
    if (s.length > 60 && s.length < 200) score += 2; // sweet spot length
    if (/[!?]/.test(s)) score += 1; // emotional punctuation
    return { sentence: s, score };
  });

  const best = scored.sort((a, b) => b.score - a.score)[0];

  // Only return if genuinely worth quoting
  if (best.score < 3) return '';

  // Truncate very long quotes
  return best.sentence.length > 180
    ? best.sentence.substring(0, 177) + '...'
    : best.sentence;
}

// ─── MAIN BUILDER ─────────────────────────────────────────────────────────────

/**
 * Build a story timeline from messages older than the AI's direct view window.
 * Returns a formatted string to inject into the system prompt.
 *
 * @param {Array}  olderMessages       - Messages BEFORE the recent window (oldest first)
 * @param {Object} options
 * @param {number} options.maxEntries  - Max timeline entries to include (default: 22)
 * @param {number} options.charLimit   - Max chars per message in timeline (default: 450)
 * @param {string} options.charName    - Character name (for formatting)
 * @param {string} options.playerName  - Player name (for formatting)
 * @returns {string} Formatted timeline section, or '' if nothing useful
 */
export function buildStoryTimeline(olderMessages, options = {}) {
  // The recent-window split is the CALLER's job — this only ever receives the
  // older slice, so a recentWindowSize option here had nothing to act on.
  const {
    maxEntries     = 22,
    charLimit      = 450,
    charName       = 'the character',
    playerName     = 'the player',
  } = options;

  if (!olderMessages || olderMessages.length === 0) return '';

  const total = olderMessages.length;

  // ── Selection: must-includes + importance-ranked rest ────────────────────
  // Always include the first 3 (scene opening establishes tone and setup)
  // and the last 3 (bridge to the recent window — most immediately relevant)
  const ANCHOR_START = Math.min(3, total);
  const ANCHOR_END   = Math.min(3, total - ANCHOR_START);

  const anchorStartIdx = new Set(Array.from({ length: ANCHOR_START }, (_, i) => i));
  const anchorEndIdx   = new Set(Array.from({ length: ANCHOR_END }, (_, i) => total - ANCHOR_END + i));
  const anchorIdx      = new Set([...anchorStartIdx, ...anchorEndIdx]);

  // Score the middle messages
  const middleScored = olderMessages
    .map((m, i) => ({
      msg: m,
      index: i,
      score: anchorIdx.has(i) ? Infinity : scoreMessage(getText(m), i, total),
    }))
    .filter(({ index }) => !anchorIdx.has(index))
    .sort((a, b) => b.score - a.score);

  const middleSlots = maxEntries - anchorIdx.size;
  const selectedMiddle = new Set(middleScored.slice(0, Math.max(0, middleSlots)).map(e => e.index));

  // Merge all selected indices in chronological order
  const selectedIdx = [...anchorIdx, ...selectedMiddle].sort((a, b) => a - b);

  // ── Formatting ────────────────────────────────────────────────────────────
  const lines = [];
  let prevIdx = -1;

  for (const idx of selectedIdx) {
    const msg     = olderMessages[idx];
    const text    = getText(msg);
    if (!text || text.length < 20) continue;

    const speaker = getSpeaker(msg, charName, playerName);
    const beat    = classifyBeat(text);
    const quote   = extractKeyQuote(text);

    // Gap indicator: how many messages were skipped?
    if (prevIdx !== -1 && idx - prevIdx > 1) {
      const skipped = idx - prevIdx - 1;
      lines.push(`  [· · · ${skipped} message${skipped > 1 ? 's' : ''} · · ·]`);
    }

    const truncated = text.length > charLimit
      ? text.substring(0, charLimit - 3) + '...'
      : text;

    const beatLabel = beat ? ` ${beat}` : '';
    // Date prefix — anchors recall to a specific moment so the AI doesn't
    // mix up "when" things happened. Uses the message's createdAt /
    // created_at, formatted as YYYY-MM-DD HH:MM. Drops cleanly to empty
    // string when timestamps aren't available.
    const ts = msg.createdAt || msg.created_at || msg.timestamp;
    let datePrefix = '';
    if (ts) {
      try {
        const d = new Date(ts);
        if (!isNaN(d.getTime())) {
          const pad = (n) => String(n).padStart(2, '0');
          datePrefix = `[${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}] `;
        }
      } catch { /* non-ISO timestamp — skip */ }
    }
    lines.push(`${datePrefix}${speaker}${beatLabel}: ${truncated}`);

    // If the key quote is substantially different from the full text shown, add it
    if (quote && text.length > charLimit && !truncated.includes(quote.substring(0, 40))) {
      lines.push(`  → Key quote: "${quote}"`);
    }

    prevIdx = idx;
  }

  if (lines.length === 0) return '';

  const headerNote = total > maxEntries
    ? `(${total} messages total — ${selectedIdx.length} key moments preserved below, with chronological gaps noted. The most important dialogue is shown verbatim. The recent messages follow after this section.)`
    : `(Earlier messages from this conversation, preserved for continuity:)`;

  return `\nSTORY HISTORY — EVERYTHING THAT HAPPENED BEFORE THE RECENT MESSAGES:\n${headerNote}\n\n${lines.join('\n')}\n\n[─── END OF STORY HISTORY — RECENT MESSAGES FOLLOW ───]`;
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────

/**
 * Extract text from a message regardless of whether it's a DM message
 * ({content: ...}), an RP message ({text: ...}), or a channel message.
 */
function getText(msg) {
  if (!msg) return '';
  return (msg.text || msg.content || '').trim();
}

/**
 * Determine who spoke this message.
 * Handles DM format ({sender_type: 'user'|'character'}) and RP format ({isPlayer: bool, name: ...}).
 */
function getSpeaker(msg, charName, playerName) {
  if (!msg) return '?';
  // RP format
  if (typeof msg.isPlayer === 'boolean') {
    return msg.isPlayer ? playerName : (msg.name || charName);
  }
  // DM format
  if (msg.sender_type === 'user') return playerName;
  if (msg.sender_type === 'character') return charName;
  // Channel format
  if (msg.senderProfile?.display_name) return msg.senderProfile.display_name;
  return charName;
}

// ─── DM-SPECIFIC BUILDER ─────────────────────────────────────────────────────

/**
 * Build a timeline for DM conversations where the loaded history
 * exceeds the AI's conversation window.
 *
 * Call this when history.length > windowSize.
 * The result goes BEFORE the conversation window in the system prompt.
 *
 * @param {Array}  history     - All loaded DM messages (oldest first)
 * @param {number} windowSize  - How many messages the AI sees directly (default: 60)
 * @param {string} charName
 * @param {string} playerName
 * @returns {string}
 */
export function buildDMTimeline(history, windowSize = 60, charName = '', playerName = '') {
  if (!history || history.length <= windowSize) return '';

  const olderMessages = history.slice(0, -windowSize);

  return buildStoryTimeline(olderMessages, {
    maxEntries: 25,
    charLimit: 400,
    charName,
    playerName,
    recentWindowSize: windowSize,
  });
}

// ─── RP-SPECIFIC BUILDER ──────────────────────────────────────────────────────

/**
 * Build a timeline for RP scenes where the in-memory history
 * exceeds the recent conversation window.
 *
 * @param {Array}  history     - Full RP message history (oldest first)
 * @param {number} windowSize  - How many messages the AI sees directly (default: 25)
 * @param {string} charName
 * @param {string} playerName
 * @returns {string}
 */
export function buildRPTimeline(history, windowSize = 25, charName = '', playerName = '') {
  if (!history || history.length <= windowSize) return '';

  const olderMessages = history.slice(0, -windowSize);

  return buildStoryTimeline(olderMessages, {
    maxEntries: 28,
    charLimit: 500,
    charName,
    playerName,
    recentWindowSize: windowSize,
  });
}
